import { Button, IconButton, Modal, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Link2Off, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  chatCreateShareLinkMutation,
  chatDeactivateShareLinkMutation,
  chatGetSessionSharingOptions,
  chatGetSessionSharingQueryKey,
  chatRemoveShareMutation,
  chatShareWithUserMutation,
  errorMessage,
} from "@/api";
import { UserSearchCombobox } from "@/components/build/UserSearchCombobox";
import { Text } from "@/components/Text";
import { ShareWithSpace } from "./ShareWithSpace";

/** Share link URL in this app (`/chat/shared/:token` resolves it and opens the chat). */
export const shareUrl = (token: string) => `${globalThis.location.origin}/chat/shared/${token}`;

/** Share a chat read-only with people, or with anyone in the org who has the link. */
export function ShareChatDialog({
  sessionUuid,
  open,
  onClose,
}: Readonly<{ sessionUuid: string; open: boolean; onClose: () => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "share" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = { session_uuid: sessionUuid };
  const sharing = useQuery({ ...chatGetSessionSharingOptions({ path }), enabled: open, select: (r) => r.data });
  const refresh = () => queryClient.invalidateQueries({ queryKey: chatGetSessionSharingQueryKey({ path }) });
  const onError = (error: unknown) => toast.error(errorMessage(error, t("failed")));
  const addUser = useMutation({ ...chatShareWithUserMutation(), onSuccess: refresh, onError });
  const removeUser = useMutation({ ...chatRemoveShareMutation(), onSuccess: refresh, onError });
  const createLink = useMutation({ ...chatCreateShareLinkMutation(), onSuccess: refresh, onError });
  const deactivateLink = useMutation({ ...chatDeactivateShareLinkMutation(), onSuccess: refresh, onError });

  const link = sharing.data?.links?.find((l) => l.isActive);
  const shares = sharing.data?.shares ?? [];

  const copy = async (token: string) => {
    await navigator.clipboard?.writeText(shareUrl(token));
    toast.success(t("copied"));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("title")}
      closeLabel={t("close")}
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t("done")}
        </Button>
      }
    >
      <div className="space-y-6">
        {sharing.data?.spaceName && (
          <ShareWithSpace
            sessionUuid={sessionUuid}
            spaceName={sharing.data.spaceName}
            shared={Boolean(sharing.data.sharedWithSpace)}
          />
        )}
        <section className="space-y-2">
          <Text variant="h3" as="h3">
            {t("linkHeading")}
          </Text>
          <Text tone="secondary">{t("linkHelp")}</Text>
          {link ? (
            <div className="flex items-center gap-2">
              <Text
                as="code"
                variant="code"
                tone="secondary"
                className="min-w-0 flex-1 truncate rounded-md bg-surface-secondary px-2 py-1.5"
              >
                {shareUrl(link.token)}
              </Text>
              <IconButton
                icon={<Copy />}
                aria-label={t("copy")}
                variant="ghost"
                onClick={() => void copy(link.token)}
              />
              <IconButton
                icon={<Link2Off />}
                aria-label={t("turnOff")}
                variant="ghost"
                onClick={() => deactivateLink.mutate({ path: { ...path, link_uuid: link.uuid } })}
              />
            </div>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              loading={createLink.isPending}
              onClick={() => createLink.mutate({ path, body: { permission: "read" } })}
            >
              {t("createLink")}
            </Button>
          )}
        </section>
        <section className="space-y-2">
          <Text variant="h3" as="h3">
            {t("peopleHeading")}
          </Text>
          <UserSearchCombobox
            clearOnSelect
            excludeUuids={shares.map((s) => s.sharedWith.uuid)}
            onSelect={(user) => {
              // Clearing the field reports an empty user; there's nobody to share with.
              if (user.uuid) addUser.mutate({ path, body: { userUuid: user.uuid, permission: "read" } });
            }}
          />
          {shares.length === 0 ? (
            <Text tone="tertiary">{t("nobody")}</Text>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {shares.map((share) => (
                <li key={share.uuid} className="flex items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <Text>{share.sharedWith.name}</Text>
                    <Text variant="caption" tone="tertiary">
                      {share.sharedWith.email}
                    </Text>
                  </div>
                  <IconButton
                    icon={<X />}
                    aria-label={t("remove", { name: share.sharedWith.name })}
                    variant="ghost"
                    size="sm"
                    onClick={() => removeUser.mutate({ path: { ...path, user_uuid: share.sharedWith.uuid } })}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}
